/**
 * constants/defaultSettings.js
 * Mirrors the DEFAULTS object in wc-product-gallery.js so the UI has
 * sensible fallbacks when settings have not been saved yet.
 */

export const DEFAULT_SETTINGS = {
    // ── General ──────────────────────────────────────────────────────────────
    variationImageChange: true,
    variationGallery: true,
    preloadImages: true,
    zoomBeforeLoad: false,
    spinner: false,
    spinnerColor: "rgba(0, 0, 0, 0.82)",

    // ── Zoom ─────────────────────────────────────────────────────────────────
    zoomType: "inner", // 'lens' | 'window' | 'inner'
    zoomLevel: 2.5,
    zoomOnHover: true,
    zoomCursor: "crosshair", // 'crosshair' | 'zoom-in' | 'default'
    zoomEasing: true,
    zoomEasingAmount: 12,

    // ── Lens ─────────────────────────────────────────────────────────────────
    lensSize: 130,
    lensShape: "circle", // 'circle' | 'square'
    lensBorderColor: "#ffffff",
    lensBorderWidth: 2,

    // ── Zoom Window ───────────────────────────────────────────────────────────
    zoomWindowSizeMode: "custom", // 'custom' | 'auto' | 'match'
    zoomWindowWidth: 400,
    zoomWindowHeight: 400,
    zoomWindowPosition: "right", // 'right' | 'left' | 'top' | 'bottom'
    zoomWindowOffsetX: 14,
    zoomWindowOffsetY: 0,
    zoomWindowBorderRadius: 8,
    zoomWindowBorderColor: "#e0e0e0",
    zoomWindowBorderWidth: 1,
    zoomWindowShadow: true,
    zoomWindowFadeEasing: true,
    zoomWindowFadeEasingAmount: 300,

    // Cursor overlay on main image (window zoom only)
    zoomWindowCursor: true,
    zoomWindowCursorSizeMode: "auto", // 'auto' | 'custom'
    zoomWindowCursorWidth: 100,
    zoomWindowCursorHeight: 100,
    zoomWindowCursorBorderRadius: 4,
    zoomWindowCursorBg: "rgba(255,255,255,0.50)",
    zoomWindowCursorBorderColor: "#ffffff",
    zoomWindowCursorBorderWidth: 1,

    // ── Gallery / Thumbnails ──────────────────────────────────────────────────
    galleryType: "thumbnail", // 'thumbnail' | 'anchor' | 'sticky-split' | 'hierarchy-grid'
    mainImageSize: "large", // WP registered image size for the main display image
    thumbnailImageSize: "woocommerce_gallery_thumbnail", // WP registered image size for thumbnails
    thumbnailShow: true, // show or hide the thumbnail strip entirely
    thumbnailPosition: "bottom", // 'bottom' | 'top' | 'left' | 'right'
    thumbnailLayout: "slider", // 'slider' | 'grid'
    thumbnailWidth: 80,
    thumbnailHeight: 80,
    thumbnailGap: 8,
    thumbnailStripGap: 8,
    thumbnailBorderRadius: 4,
    thumbnailActiveBorderColor: "rgba(0, 0, 0, 0.82)",
    thumbnailActiveBorderWidth: 2,
    thumbnailSliderButtons: true,
    thumbnailSlideType: "row",
    thumbnailLoop: false,
    thumbnailDragNav: true,
    thumbnailScrollNav: true,
    thumbnailVisible: 5,
    thumbnailGridColumns: 4,
    thumbnailFitToImage: true,
    thumbnailTrigger: "click",
    thumbnailArrowsOverlay: false,
    thumbnailBreakpoint: 1025,
    thumbnailAlignment: "left", // 'left' | 'center' | 'right' (horizontal strips only)
    thumbnailAnimation: true, // staggered fade-in entrance animation for thumbnail items

    // ── Main Image ────────────────────────────────────────────────────────────
    mainImageHeightMode: "lock", // 'lock' | 'adaptive' | 'fixed'
    mainImageHeight: 0, // px, used when mainImageHeightMode === 'fixed'
    mainImageFit: "contain", // 'contain' | 'cover'
    mainImageBackground: "#f7f7f7",
    mainImageBorderRadius: 6,
    mainImageBorderWidth: 0,
    mainImageBorderColor: "#e0e0e0",
    mainImageShadow: false,
    mainImageArrows: true,
    mainImageArrowsAutoHide: true,
    mainImagePagination: true,
    mainImagePaginationAutoHide: true,
    mainImagePaginationType: "dots", // 'dots' | 'progressbar'
    mainImagePaginationPosition: "bottom", // 'bottom' | 'left' | 'right'
    mainImageSwipe: false, // swipe left/right on main image to navigate (requires zoomOnHover and touchZoom both disabled)
    mainImageScrollNav: false, // scroll wheel on main image to navigate next/prev
    mainImageScrollNavDelay: 700, // ms cooldown between scroll-triggered navigations

    // Stacked layout (Anchor Navigation mode)
    stackedItemGap: 24,
    stackedNavigationType: "dots", // 'dots' | 'thumbs'
    anchorZoom: true,
    stackedThumbsStickyTop: 40,
    stackedDotsPosition: "outside", // 'overlay' | 'outside'
    stackedDotGap: 8,
    stackedDotSize: 8,
    stackedDotColor: "rgba(0, 0, 0, 0.5)",
    stackedDotActiveColor: "#000000",

    // Stacked layout — sticky product summary
    stackedStickyEnabled: false,
    stackedStickySelector: ".summary.entry-summary",
    stackedStickyTop: 100,
    stackedStickyBreakpoint: 1024,
    stackedStickyFixOverflow: true,

    // Sticky Split layout
    stickySplitMainSide: "left",
    stickySplitMainWidthPercent: 65,
    stickySplitGap: 20,
    stickySplitStickyTop: 50,
    stickySplitScrollItemGap: 20,
    stickySplitScrollItemAspect: "auto",
    stickySplitScrollItemFit: "cover",
    stickySplitScrollItemBorderRadius: 6,
    stickySplitShowCaption: false,
    stickySplitClickAction: "lightbox",
    stickySplitClickSync: false,
    stickySplitThumbZoom: false,

    // Hierarchy Grid layout
    hierarchyGridColumns: 2,
    hierarchyGridGap: 16,
    hierarchyGridItemAspect: "1/1",
    hierarchyGridItemFit: "cover",
    hierarchyGridItemBorderRadius: 6,
    hierarchyGridZoom: true,

    // Tiles layout
    tilesColumns: 2,
    tilesGap: 8,
    tilesItemAspect: "3/2",
    tilesItemFit: "cover",
    tilesItemBorderRadius: 0,
    tilesInitialCount: 6,
    tilesShowMoreEnabled: true,
    tilesShowMoreText: "Show More",
    tilesShowLessText: "Show Less",
    tilesZoom: true,

    mobileBreakpoint: 768,
    mobileShowThumbs: false,
    mobileThumbnailPosition: "bottom", // 'bottom' | 'top' | 'left' | 'right'
    mobileThumbnailWidth: 100,
    mobileThumbnailHeight: 100,
    mobileArrows: true,
    mobilePagination: true,
    mobilePaginationPosition: "bottom",

    mainImageTransition: "slide", // 'fade' | 'slide' | 'zoom' | 'none'
    mainImageTransitionDuration: 500,
    mainImageSlideDirection: "horizontal", // 'horizontal' | 'vertical' (slide transition only)

    // ── Lightbox ──────────────────────────────────────────────────────────────
    lightbox: true,
    lightboxOnClick: true,
    lightboxButtonAutoHide: true,
    lightboxOpenOnImageClick: false,
    lightboxLoop: true,
    lightboxShowCounter: true,
    lightboxShowCaption: true,
    lightboxShowThumbnails: true,
    lightboxPaginationType: "thumbs",
    lightboxCloseOnOverlay: true,
    lightboxKeyboard: true,
    lightboxAnimationDuration: 320,
    lightboxTransition: "slide", // 'slide' | 'fade' | 'zoom' | 'none'
    lightboxAutoplay: false,
    lightboxAutoplayDelay: 3500,
    lightboxShowAutoplayButton: true,
    lightboxZoomIn: true,
    lightboxScrollZoom: true,
    lightboxFullscreen: true,
    lightboxShowArrows: true,
    lightboxSwipe: true,
    lightboxSingleImage: false,
    lightboxImageBorderRadius: 6,
    lightboxThumbBorderRadius: 5,
    lightboxZoomOrigin: false,
    lightboxImageTools: true,
    lightboxShare: false,
    lightboxShareFacebook: true,
    lightboxShareTwitter: true,
    lightboxSharePinterest: true,
    lightboxShareDownload: true,

    // ── Mobile / Touch ────────────────────────────────────────────────────────
    touchZoom: true,
    touchZoomMin: 1,
    touchZoomMax: 5,
    swipeThreshold: 50,

    // ── Video ─────────────────────────────────────────────────────────────────
    videoPosition: "end", // 'start' | 'end' — where product videos appear in the thumbnail strip
    videoAutoplay: false, // autoplay embed when navigated to
    videoMuted: true, // mute by default (required for browser autoplay)
    videoLoop: false, // loop YouTube / Vimeo embeds
    videoHideRelated: true, // hide related videos at end (YouTube rel=0)
    videoEmbedControls: true, // show player controls on YouTube / Vimeo embeds
    videoEmbedFitContainer: false, // fill the main image area (crop embed to match container)
    videoControls: true, // show controls on hosted <video> elements
    videoHostedLoop: false, // loop hosted (uploaded) video files
    videoHostedAutoplay: false, // autoplay hosted video when navigated to
    videoHostedMuted: false, // mute hosted video (useful with autoplay)
    videoHostedFitContainer: false, // fill the main image area (crop hosted video to match container)
};
